import classes from "./SourcesByMetric.module.css";

export type SourceGroup = {
  title: string;
  items: string[];
};

/** Rough per-group height, used to balance the columns and decide page spill. */
const GROUP_HEADING_H = 14;
const ITEM_LINE_H = 9.5;
const ITEM_GAP = 2;
const GROUP_GAP = 7;
/** Characters that fit on one line of an ~350px column at this font size. */
const CHARS_PER_LINE = 92;

export const estimateGroupHeight = (group: SourceGroup): number =>
  GROUP_HEADING_H +
  group.items.reduce(
    (sum, item) =>
      sum +
      ITEM_GAP +
      Math.max(1, Math.ceil(item.length / CHARS_PER_LINE)) * ITEM_LINE_H,
    0
  ) +
  GROUP_GAP;

/**
 * Splits groups into what fits in a two-column region of `maxHeight` and what
 * has to spill onto another page.
 *
 * Estimated rather than measured, deliberately: the page must be right on its
 * first render because GeneratePDF rasterizes immediately, and a measure-then-
 * reflow pass would not have settled in time.
 */
export const splitSourceGroups = (
  groups: SourceGroup[],
  maxHeight: number
): { fitted: SourceGroup[]; overflow: SourceGroup[] } => {
  // Two columns, so the region holds roughly twice a single column's height.
  const capacity = maxHeight * 2;
  let used = 0;
  const fitted: SourceGroup[] = [];
  const overflow: SourceGroup[] = [];

  groups.forEach((group) => {
    const height = estimateGroupHeight(group);
    if (overflow.length === 0 && used + height <= capacity) {
      used += height;
      fitted.push(group);
    } else {
      overflow.push(group);
    }
  });

  return { fitted, overflow };
};

interface Props {
  groups: SourceGroup[];
}

/**
 * Scientific sources grouped by metric across two balanced columns.
 *
 * Not the shared Qrcode source list: that splits a flat array in half with no
 * grouping and always renders the QR image alongside.
 *
 * Explicit flex columns rather than `column-count: 2` — html2canvas's multi-column
 * support is unreliable, so a CSS-column layout could reflow in the PDF only.
 */
const SourcesByMetric = ({ groups }: Props): JSX.Element => {
  // Greedy balance: fill the first column until it passes half the total.
  const total = groups.reduce((sum, g) => sum + estimateGroupHeight(g), 0);
  const half = total / 2;

  const columnA: SourceGroup[] = [];
  const columnB: SourceGroup[] = [];
  let running = 0;

  groups.forEach((group) => {
    const height = estimateGroupHeight(group);
    // Keep a group in column A while its midpoint is still in the top half, so a
    // single tall group cannot push everything into column B.
    if (running + height / 2 <= half) {
      columnA.push(group);
    } else {
      columnB.push(group);
    }
    running += height;
  });

  const renderColumn = (column: SourceGroup[]) => (
    <div className={classes.column}>
      {column.map((group) => (
        <div className={classes.group} key={group.title}>
          <div className={classes.groupTitle}>{group.title}</div>
          {group.items.map((item, index) => (
            <div className={classes.item} key={index}>
              <span className={classes.bullet} />
              <span className={classes.itemText}>{item}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={classes.columns}>
      {renderColumn(columnA)}
      {renderColumn(columnB)}
    </div>
  );
};

export default SourcesByMetric;
